import React, { useState, useEffect } from 'react';

const FEE_RATE = 0.001425;
const TAX_RATE_NORMAL = 0.003;
const TAX_RATE_DAY_TRADE = 0.0015;

// 計算符合台股跳檔的合法價格
const getValidTickPrice = (price) => {
    let p = Number(price);
    if (isNaN(p) || p <= 0) return 0.0;
    
    let tick = 0.01;
    if (p <= 10) tick = 0.01;
    else if (p <= 50) tick = 0.05;
    else if (p <= 100) tick = 0.1;
    else if (p <= 500) tick = 0.5;
    else if (p <= 1000) tick = 1.0;
    else tick = 5.0;
    
    return Math.ceil(p / tick) * tick;
};

export default function StockCalculator() {
    // 表單狀態
    const [buyPrice, setBuyPrice] = useState('');
    const [sellPrice, setSellPrice] = useState('');
    const [shares, setShares] = useState(1);
    const [discount, setDiscount] = useState(1.0);
    const [minFee, setMinFee] = useState(20);
    const [isDayTrade, setIsDayTrade] = useState(false);

    // 計算結果狀態
    const [breakEvenPrice, setBreakEvenPrice] = useState(null);
    const [result, setResult] = useState(null);

    // 自動計算損益兩平價
    useEffect(() => {
        const pBuy = parseFloat(buyPrice);
        const pShares = parseFloat(shares);
        const pDiscount = parseFloat(discount);
        const pMinFee = parseInt(minFee) || 20;

        if (isNaN(pBuy) || pBuy <= 0 || isNaN(pShares) || pShares <= 0 || isNaN(pDiscount)) {
            setBreakEvenPrice(null);
            return;
        }

        const totalShares = Math.round(pShares * 1000);
        const buyAmount = Math.floor(pBuy * totalShares);
        const buyFee = Math.max(pMinFee, Math.floor(buyAmount * FEE_RATE * pDiscount));
        const totalCost = buyAmount + buyFee;

        const taxRate = isDayTrade ? TAX_RATE_DAY_TRADE : TAX_RATE_NORMAL;
        let estimatedSellAmount = totalCost / (1 - FEE_RATE * pDiscount - taxRate);
        let sellAmount = Math.floor(estimatedSellAmount);

        // 微調尋找最準確的賣出金額
        while (true) {
            let sFee = Math.max(pMinFee, Math.floor(sellAmount * FEE_RATE * pDiscount));
            let sTax = Math.floor(sellAmount * taxRate);
            let netRev = sellAmount - sFee - sTax;
            if (netRev >= totalCost) break;
            sellAmount += 1;
        }

        const rawPrice = sellAmount / totalShares;
        setBreakEvenPrice(getValidTickPrice(rawPrice).toFixed(2));
    }, [buyPrice, shares, discount, minFee, isDayTrade]);

    // 手動計算詳細損益
    const handleCalculate = () => {
        const pBuy = parseFloat(buyPrice);
        const pSell = parseFloat(sellPrice);
        const pShares = parseFloat(shares);
        const pDiscount = parseFloat(discount);
        const pMinFee = parseInt(minFee) || 20;

        if (isNaN(pBuy) || isNaN(pSell) || isNaN(pShares)) {
            alert("請填寫完整的價格與張數！(請勿使用文字)"); // 在React中若需替換alert，可改用自訂Modal狀態
            return;
        }

        const totalShares = Math.round(pShares * 1000);
        const buyAmount = Math.floor(pBuy * totalShares);
        const sellAmount = Math.floor(pSell * totalShares);

        const buyFee = Math.max(pMinFee, Math.floor(buyAmount * FEE_RATE * pDiscount));
        const sellFee = Math.max(pMinFee, Math.floor(sellAmount * FEE_RATE * pDiscount));
        
        const taxRate = isDayTrade ? TAX_RATE_DAY_TRADE : TAX_RATE_NORMAL;
        const sellTax = Math.floor(sellAmount * taxRate);

        const totalCost = buyAmount + buyFee;
        const netRevenue = sellAmount - sellFee - sellTax;
        const netProfit = netRevenue - totalCost;
        const returnRate = totalCost > 0 ? (netProfit / totalCost) * 100 : 0;

        setResult({
            totalCost,
            netRevenue,
            buyFee,
            sellFee,
            sellTax,
            netProfit,
            returnRate: returnRate.toFixed(2)
        });
    };

    return (
        <div className="bg-slate-950 text-slate-100 min-h-screen flex flex-col items-center justify-center p-4 font-sans">
            <div className="w-full max-w-xl bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-center">
                    <h1 className="text-2xl font-bold tracking-tight text-white">台股智慧損益計算機</h1>
                    <p className="text-blue-100 text-sm mt-1">支援一般交易、當沖、零股及台股跳檔自動對齊</p>
                </div>

                <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-1">買入價格 (元)</label>
                            <input 
                                type="number" step="0.01" placeholder="例如: 214.5" 
                                value={buyPrice} onChange={(e) => setBuyPrice(e.target.value)}
                                className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-1">賣出價格 (元)</label>
                            <input 
                                type="number" step="0.01" placeholder="例如: 227.5" 
                                value={sellPrice} onChange={(e) => setSellPrice(e.target.value)}
                                className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-1">交易張數</label>
                            <input 
                                type="number" step="0.01" placeholder="可輸入小數(零股)" 
                                value={shares} onChange={(e) => setShares(e.target.value)}
                                className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-1">手續費折扣</label>
                            <input 
                                type="number" step="0.01" placeholder="例如: 0.35" 
                                value={discount} onChange={(e) => setDiscount(e.target.value)}
                                className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-1">最低手續費</label>
                            <input 
                                type="number" 
                                value={minFee} onChange={(e) => setMinFee(e.target.value)}
                                className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between bg-slate-950 p-4 rounded-xl border border-slate-800">
                        <label className="flex items-center space-x-3 cursor-pointer">
                            <input 
                                type="checkbox" 
                                checked={isDayTrade} onChange={(e) => setIsDayTrade(e.target.checked)}
                                className="w-5 h-5 text-blue-600 rounded bg-slate-900 border-slate-700 focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-slate-200">當沖交易 (稅率減半 0.15%)</span>
                        </label>
                        <div className="text-right">
                            <span className="text-sm font-bold text-cyan-400">
                                損益兩平賣價: {breakEvenPrice !== null ? `${breakEvenPrice}` : '--'}
                            </span>
                        </div>
                    </div>

                    <button 
                        onClick={handleCalculate}
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold py-3 px-6 rounded-xl shadow-lg transition transform active:scale-95"
                    >
                        立即計算詳細損益
                    </button>

                    {result && (
                        <div className="space-y-4 border-t border-slate-800 pt-6 animate-fade-in-up">
                            <h2 className="text-lg font-semibold text-white">計算結果報表</h2>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-slate-950 p-4 rounded-xl border border-slate-800">
                                    <span className="text-xs text-slate-400 block">買入總成本</span>
                                    <span className="text-lg font-bold text-white">{result.totalCost.toLocaleString()} 元</span>
                                </div>
                                <div className="bg-slate-950 p-4 rounded-xl border border-slate-800">
                                    <span className="text-xs text-slate-400 block">賣出淨收入</span>
                                    <span className="text-lg font-bold text-white">{result.netRevenue.toLocaleString()} 元</span>
                                </div>
                            </div>

                            <div className="bg-slate-950 p-4 rounded-xl border border-slate-800 space-y-2 text-sm">
                                <div className="flex justify-between text-slate-300">
                                    <span>買入手續費:</span>
                                    <span className="font-mono">{result.buyFee.toLocaleString()} 元</span>
                                </div>
                                <div className="flex justify-between text-slate-300">
                                    <span>賣出手續費:</span>
                                    <span className="font-mono">{result.sellFee.toLocaleString()} 元</span>
                                </div>
                                <div className="flex justify-between text-slate-300">
                                    <span>賣出交易稅:</span>
                                    <span className="font-mono">{result.sellTax.toLocaleString()} 元</span>
                                </div>
                            </div>

                            {/* 動態判斷獲利或虧損顏色 */}
                            <div className={`p-4 rounded-xl border flex items-center justify-between ${
                                result.netProfit > 0 ? 'bg-red-950/30 border-red-800/50' : 
                                result.netProfit < 0 ? 'bg-emerald-950/30 border-emerald-800/50' : 
                                'bg-slate-950 border-slate-800'
                            }`}>
                                <div>
                                    <span className={`text-xs font-bold block uppercase tracking-wider ${
                                        result.netProfit > 0 ? 'text-red-400' : 
                                        result.netProfit < 0 ? 'text-emerald-400' : 'text-slate-400'
                                    }`}>
                                        淨損益
                                    </span>
                                    <span className={`text-2xl font-extrabold ${
                                        result.netProfit > 0 ? 'text-red-500' : 
                                        result.netProfit < 0 ? 'text-emerald-500' : 'text-white'
                                    }`}>
                                        {result.netProfit > 0 ? '+' : ''}{result.netProfit.toLocaleString()} 元
                                    </span>
                                </div>
                                <div className="text-right">
                                    <span className="text-xs text-slate-400 block">報酬率</span>
                                    <span className={`text-xl font-bold ${
                                        result.netProfit > 0 ? 'text-red-500' : 
                                        result.netProfit < 0 ? 'text-emerald-500' : 'text-white'
                                    }`}>
                                        {result.returnRate} %
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
